/** CSV bulk account import — shared by admin Accounts/Wholesale pages
 * and the reseller portal.
 *
 * Client-side CSV parsing (quoted-field aware), preview table with
 * per-row validation, then POST rows to the bulk endpoint which returns
 * per-row outcomes.
 */
import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

export interface ImportRow {
  type: string;
  account: string;
  balance?: string;
  credit_limit?: string;
  company?: string;
  email?: string;
  pin?: string;
}

interface ImportAccountsDialogProps {
  open: boolean;
  onClose: () => void;
  /** POST endpoint: /accounts/import (admin) or /resellers/me/import-accounts */
  endpoint: string;
  /** Called after a successful import so the caller can refetch. */
  onImported: (created: number, failed: number) => void;
  /** Fetch helper (apiClient.post or resellerFetch POST). */
  post: (endpoint: string, body: object) => Promise<any>;
}

/** Minimal CSV parser: quoted fields, embedded commas/newlines, CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((c) => c !== "")) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    if (row.some((c) => c !== "")) rows.push(row);
  }
  return rows;
}

const EXPECTED_HEADERS = [
  "type",
  "account",
  "balance",
  "credit_limit",
  "company",
  "email",
  "pin",
];

/** Basic client-side row validation for the preview. */
function validateRow(row: ImportRow): string | null {
  if (!["wholesale", "pin_test"].includes(row.type)) {
    return "type must be wholesale or pin_test";
  }
  if (!row.account.trim()) return "account is required";
  return null;
}

export default function ImportAccountsDialog({
  open,
  onClose,
  endpoint,
  onImported,
  post,
}: ImportAccountsDialogProps) {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ created: number; failed: number; results: any[] } | null>(null);

  const reset = () => {
    setRows([]);
    setFileName("");
    setParseError("");
    setResults(null);
  };

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setParseError("");
    setResults(null);
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.length < 2) {
      setParseError("CSV needs a header row and at least one data row");
      setRows([]);
      return;
    }
    const headers = parsed[0].map((h) => h.trim().toLowerCase());
    const missing = EXPECTED_HEADERS.filter((h) => !headers.includes(h));
    if (missing.length) {
      setParseError(`Missing columns: ${missing.join(", ")}`);
      setRows([]);
      return;
    }
    const out: ImportRow[] = parsed.slice(1).map((cells) => {
      const get = (name: string) => {
        const idx = headers.indexOf(name);
        return idx >= 0 ? (cells[idx] ?? "").trim() : "";
      };
      return {
        type: get("type"),
        account: get("account"),
        balance: get("balance") || undefined,
        credit_limit: get("credit_limit") || undefined,
        company: get("company") || undefined,
        email: get("email") || undefined,
        pin: get("pin") || undefined,
      };
    });
    setRows(out.slice(0, 500));
  };

  const validCount = useMemo(
    () => rows.filter((r) => validateRow(r) === null).length,
    [rows]
  );

  const handleImport = async () => {
    const payloadRows = rows.map((r) => ({
      ...r,
      balance: r.balance ? parseFloat(r.balance) : 0,
      credit_limit: r.credit_limit ? parseFloat(r.credit_limit) : undefined,
    }));
    setBusy(true);
    try {
      const response = await post(endpoint, { rows: payloadRows });
      setResults(response.data ?? response);
      onImported(response.data?.created ?? 0, response.data?.failed ?? 0);
    } catch (err: any) {
      setParseError(err.response?.data?.detail || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Import Accounts</DialogTitle>
      <DialogContent>
        {!results && (
          <>
            <Box sx={{ mt: 1, mb: 2 }}>
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                Choose CSV file
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
              </Button>
              {fileName && (
                <Typography component="span" sx={{ ml: 2 }}>
                  {fileName} — {rows.length} rows ({validCount} valid)
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Columns: <code>type,account,balance,credit_limit,company,email,pin</code> —
              type is <code>wholesale</code> or <code>pin_test</code>; PIN accounts are
              prefixed <code>pin_</code> automatically. Max 500 rows.
            </Typography>
          </>
        )}

        {parseError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseError}
          </Alert>
        )}

        {rows.length > 0 && !results && (
          <Box sx={{ maxHeight: 300, overflow: "auto", mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const vErr = validateRow(r);
                  return (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.type}</TableCell>
                      <TableCell>{r.account}</TableCell>
                      <TableCell>{r.company || "-"}</TableCell>
                      <TableCell>
                        {vErr ? (
                          <Chip label={vErr} size="small" color="error" />
                        ) : (
                          <Chip label="OK" size="small" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {results && (
          <>
            <Alert severity={results.failed === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
              {results.created} created, {results.failed} failed
            </Alert>
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {results.results.map((r: any) => (
                <Box key={r.row} sx={{ display: "flex", gap: 1, py: 0.25 }}>
                  <Chip
                    label={r.status}
                    size="small"
                    color={r.status === "created" ? "success" : "error"}
                  />
                  <Typography variant="body2">
                    Row {r.row}: {r.id || r.error}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{results ? "Done" : "Cancel"}</Button>
        {!results && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={validCount === 0 || busy}
            startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          >
            Import {validCount} accounts
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
